import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import axios from 'axios';

import { addAxiosMsalTokenInterceptor } from '../auth/utils/axios-interceptor';

export function serializeAxiosParams(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      }
    } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      searchParams.set(key, String(value));
    }
  }

  return searchParams.toString();
}

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://localhost:5001/api/v1', // Fallback to localhost if not defined
  timeout: 30000, // Matching the backend timeout and above the ~13s worst-case EF Core retry window so cold-start 503s aren't aborted client-side first
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  // ASP.NET Core binds List<T> from repeated keys (groupFields=a&groupFields=b).
  // Axios v1 defaults to bracket notation (groupFields[]=a) which ASP.NET Core does not match.
  paramsSerializer: serializeAxiosParams,
});

// Request interceptor
addAxiosMsalTokenInterceptor(api);

type RetryableRequestConfig = InternalAxiosRequestConfig & { __retryCountTransientUnavailable?: number };

const MAX_TRANSIENT_UNAVAILABLE_RETRIES = 2;

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - maybe redirect to login
    }

    const config = error.config as RetryableRequestConfig | undefined;
    // Retry-After is set exclusively by the backend for transient DB cold-start 503s (see
    // ExceptionHandlingMiddleware); infrastructure-level 503s never set it, so they are not retried here.
    const retryAfterHeader = error.response?.headers?.['retry-after'];
    const retryCount = config?.__retryCountTransientUnavailable ?? 0;
    const isRetryableTransientUnavailable =
      error.response?.status === 503 && retryAfterHeader !== undefined && config && retryCount < MAX_TRANSIENT_UNAVAILABLE_RETRIES;

    if (isRetryableTransientUnavailable) {
      config.__retryCountTransientUnavailable = retryCount + 1;
      const delaySeconds = Number.parseInt(String(retryAfterHeader), 10);
      const delayMs = (Number.isFinite(delaySeconds) ? delaySeconds : 3) * 1000;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      return api.request(config);
    }

    throw error;
  }
);

export default api;
